/**
 * Support API client (Build 3, UI side).
 *
 * Thin wrappers over the shared `apiFetch` helper (see ./api) that hit the
 * SAME-ORIGIN ChMS proxy routes under `/api/support/*` with the session cookie.
 * The ChMS backend (NOT the browser) signs and forwards these to the external
 * FC EMS support system, deriving the principal org + submitter email
 * server-side. The browser never talks to EMS directly and never supplies the
 * tenant org or submitter identity.
 *
 * The ticket DTOs below mirror the EMS-over-the-wire shapes that the proxy
 * passes through verbatim (see apps/api/src/modules/support/ems-client.ts).
 * They are kept open (`[extra: string]: unknown`) because EMS is authoritative
 * and may add fields.
 */
import { apiFetch } from './api'

// ----------------------------------------------------------- DTOs (EMS shapes)

export type TicketPriority = 'low' | 'normal' | 'high' | 'urgent'

/** Body accepted by POST /support/tickets (org + submitter come from session). */
export interface CreateTicketInput {
  title: string
  description: string
  priority?: TicketPriority
}

/** 201 response from POST /support/tickets. */
export interface CreateTicketResult {
  ticket_id: string
  status: string
  created_at: string
}

/** A row in the list response. EMS may include extra fields (kept open). */
export interface TicketSummary {
  ticket_id: string
  title: string
  status: string
  priority: TicketPriority | string
  updated_at: string
  submitter_email: string
  [extra: string]: unknown
}

/** GET /support/tickets response (scoped to the signed org). */
export interface TicketListResult {
  tickets: TicketSummary[]
  page: number
  total: number
}

/** GET /support/tickets/{id} response — the full ticket (open shape). */
export interface TicketDetail {
  ticket_id: string
  title: string
  status: string
  priority: TicketPriority | string
  updated_at: string
  submitter_email: string
  description?: string
  created_at?: string
  [extra: string]: unknown
}

/** Query params for listing tickets (both optional). */
export interface ListTicketsQuery {
  status?: string
  page?: number
}

/**
 * What the current admin may do with support, derived server-side from their
 * org-scoped permissions (`support.submit` -> canSubmit, `support.view` ->
 * canView). View-only admins see the list + detail but no submit form.
 */
export interface SupportAccess {
  canSubmit: boolean
  canView: boolean
}

// ------------------------------------------------------------------- API

/** Whether support is reachable + what the current admin may do. */
export const getSupportAccess = (): Promise<SupportAccess> =>
  apiFetch<SupportAccess>('/support/access')

/** List the current org's tickets (optionally filtered by status / paged). */
export const listTickets = (query: ListTicketsQuery = {}): Promise<TicketListResult> => {
  const usp = new URLSearchParams()
  if (query.status) usp.set('status', query.status)
  if (query.page != null) usp.set('page', String(query.page))
  const qs = usp.toString()
  return apiFetch<TicketListResult>(`/support/tickets${qs ? `?${qs}` : ''}`)
}

/** Fetch one ticket by id (404 if it is not owned by the current org). */
export const getTicket = (id: string): Promise<TicketDetail> =>
  apiFetch<TicketDetail>(`/support/tickets/${encodeURIComponent(id)}`)

/** Submit a new ticket for the current org, acting as the current admin. */
export const createTicket = (input: CreateTicketInput): Promise<CreateTicketResult> =>
  apiFetch<CreateTicketResult>('/support/tickets', {
    method: 'POST',
    body: JSON.stringify(input),
  })
